import { z } from 'zod';

/**
 * The API envelope, used by EVERY response — successes, validation failures,
 * 403s and 500s alike.
 *
 *   { success: boolean, data: object | null, error: { code, message } | null }
 *
 * One shape means a client has exactly one branch to write, and a failure can
 * never arrive in a form the caller did not expect. It is defined here, once,
 * and both the server that emits it and the client that reads it are typed
 * from this file.
 */

export const errorShape = z.object({
  /** Stable, machine-readable, SCREAMING_SNAKE. Safe to switch on. */
  code: z.string(),
  /** Written for the person reading it, not for a log. */
  message: z.string(),
  /** Per-field problems, for inline form validation. */
  fields: z.record(z.string(), z.string()).optional(),
  /**
   * Machine-readable context a client may branch on beyond `code`, e.g. a
   * refused game launch's `refusal` (a GameLaunchRefusal), or the identity
   * gate's `refusal` that verifying clears (IDENTITY_NOT_VERIFIED_REFUSAL, in
   * player-money.ts). Never shown as-is.
   */
  details: z.record(z.string(), z.string()).optional(),
});

export type ApiError = z.infer<typeof errorShape>;

export const envelope = <T extends z.ZodTypeAny>(data: T) =>
  z.discriminatedUnion('success', [
    z.object({ success: z.literal(true), data, error: z.null() }),
    z.object({ success: z.literal(false), data: z.null(), error: errorShape }),
  ]);

export type Envelope<T> =
  | { success: true; data: T; error: null }
  | { success: false; data: null; error: ApiError };

/** Error codes the clients switch on. Adding one here is adding it everywhere. */
export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',
  TWO_FACTOR_INVALID: 'TWO_FACTOR_INVALID',
  CREDENTIALS_INVALID: 'CREDENTIALS_INVALID',
  MAKER_IS_CHECKER: 'MAKER_IS_CHECKER',
  INTERNAL: 'INTERNAL',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Pagination, one shape for every list endpoint. */
export const pageMeta = z.object({
  page: z.number().int().positive(),
  perPage: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});

export type PageMeta = z.infer<typeof pageMeta>;

export const paged = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), meta: pageMeta });

export const pageQuery = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().positive().max(200).default(25),
});

/**
 * Money crosses the wire as a STRING of centavos.
 *
 * JSON numbers are IEEE 754 doubles: ₱92,233,720,368,547,758.07 is beyond
 * Number.MAX_SAFE_INTEGER, and long before that the rounding is already wrong.
 * A string parses back to a bigint exactly, every time.
 */
export const centavos = z.string().regex(/^-?\d+$/, 'Expected an integer number of centavos');
export type CentavosString = z.infer<typeof centavos>;

export const toCentavos = (s: string): bigint => BigInt(s);
export const fromCentavos = (n: bigint): string => n.toString();
